/**
 * App Configuration
 */

// Cache for tenant config
let cachedTenantConfig: TenantAppConfig | null = null;
let cacheExpiry: number = 0;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// Runtime-resolved tenant ID (set once during init)
let _runtimeTenantId: string | null = null;

export interface TenantAppConfig {
  tenantId: string;
  liffId: string;
  apiBaseUrl: string;
  supportedLanguages: string[];
  defaultLanguage: string;
  features: Record<string, boolean>;
  plan?: string;
  brandingBadge?: boolean;
  brandingLink?: string;
  hotelName?: string | Record<string, string>;
}

/**
 * Get API Base URL - dynamically resolves based on current hostname
 */
export function getApiBaseUrl(): string {
  // Check environment variable first
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }

  // Server-side rendering fallback
  if (typeof window === 'undefined' || !window.location) {
    return 'http://localhost:4001';
  }

  const { protocol, hostname } = window.location;

  // Map frontend hostnames to API hostname
  const mapping: Record<string, string> = {
    'revio-miniapp-504118.web.app': 'revio-api-6331563496.asia-southeast1.run.app',
    'revio-platform-504118.web.app': 'revio-api-6331563496.asia-southeast1.run.app',
    'mini-app.metagoldtech.com': 'revio-api-6331563496.asia-southeast1.run.app',
  };

  const apiHost =
    mapping[hostname] ||
    (hostname === 'localhost' ? 'localhost:4001' : 'revio-api-6331563496.asia-southeast1.run.app');
  const url = hostname === 'localhost' ? `http://${apiHost}` : `https://${apiHost}`;

  console.log('[MiniApp Config] Resolved API_BASE:', url, '(from host:', hostname, ')');
  return url;
}

/**
 * Resolve tenant ID at runtime.
 * Priority: 1) URL ?tenant= param  2) VITE_TENANT_ID env  3) 'demo'
 */
export function getTenantId(): string {
  if (_runtimeTenantId) return _runtimeTenantId;
  return import.meta.env.VITE_TENANT_ID || '';
}

export function setRuntimeTenantId(tenantId: string): void {
  _runtimeTenantId = tenantId;
  try { sessionStorage.setItem('_tenantId', tenantId); } catch (_) { /* ignore */ }
}

export function extractLiffIdFromContext(): string | null {
  if (typeof window === 'undefined') return null;
  const LIFF_ID_REGEX = /\b(\d{10}-[A-Za-z0-9_-]{8})\b/;

  // 1. From direct query params
  const params = new URLSearchParams(window.location.search);
  const qLiff = params.get('liffId') || params.get('liff_id');
  if (qLiff && LIFF_ID_REGEX.test(qLiff)) return qLiff;

  // 2. From liff.state param
  const liffState = params.get('liff.state');
  if (liffState) {
    try {
      const decodedState = decodeURIComponent(liffState);
      const match = decodedState.match(LIFF_ID_REGEX);
      if (match) return match[1];
    } catch (_) {}
  }

  // 3. From window.location.href
  const hrefMatch = window.location.href.match(LIFF_ID_REGEX);
  if (hrefMatch) return hrefMatch[1];

  // 4. From document.referrer
  if (typeof document !== 'undefined' && document.referrer) {
    const refMatch = document.referrer.match(LIFF_ID_REGEX);
    if (refMatch) return refMatch[1];
  }

  // 5. From sessionStorage
  try {
    const stored = sessionStorage.getItem('_detected_liff_id');
    if (stored && LIFF_ID_REGEX.test(stored)) return stored;
  } catch (_) {}

  return null;
}

export async function resolveTenantByLiff(liffId: string): Promise<string | null> {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/v1/resolve-tenant/${liffId}`);
    if (!response.ok) return null;
    const result = await response.json();
    return result.success ? result.data.tenantId : null;
  } catch (error) {
    console.error('[MiniApp Config] Error resolving tenant by LIFF:', error);
    return null;
  }
}

/**
 * Detect and lock tenant ID from URL query param or dynamic LIFF reverse lookup.
 * Persists to sessionStorage so the value survives LIFF login redirects.
 * Must be called once during app init, before any API calls.
 */
export function initTenantFromUrl(): string {
  if (typeof window !== 'undefined' && window.location) {
    const params = new URLSearchParams(window.location.search);
    let tenantParam = params.get('tenant') || params.get('tenantId');

    // Decode from liff.state if present
    const liffState = params.get('liff.state');
    if (!tenantParam && liffState) {
      try {
        const decodedState = decodeURIComponent(liffState);
        const stateQuery = decodedState.startsWith('?') ? decodedState.slice(1) : (decodedState.includes('?') ? decodedState.split('?')[1] : decodedState);
        const stateParams = new URLSearchParams(stateQuery);
        tenantParam = stateParams.get('tenant') || stateParams.get('tenantId');
      } catch (_) {}
    }

    // Decode from hash if present
    if (!tenantParam && window.location.hash && window.location.hash.includes('?')) {
      try {
        const hashQuery = window.location.hash.split('?')[1];
        const hashParams = new URLSearchParams(hashQuery);
        tenantParam = hashParams.get('tenant') || hashParams.get('tenantId');
      } catch (_) {}
    }

    if (tenantParam && /^[a-zA-Z0-9_-]{1,64}$/.test(tenantParam)) {
      _runtimeTenantId = tenantParam;
      try { sessionStorage.setItem('_tenantId', tenantParam); } catch (_) { /* ignore */ }
      console.log('[MiniApp Config] Tenant from URL/state param:', tenantParam);
    }
  }
  // Recover from sessionStorage (survives LIFF login redirect that strips query params)
  if (!_runtimeTenantId) {
    try {
      const stored = sessionStorage.getItem('_tenantId');
      if (stored && /^[a-zA-Z0-9_-]{1,64}$/.test(stored)) {
        _runtimeTenantId = stored;
        console.log('[MiniApp Config] Tenant from sessionStorage:', stored);
      }
    } catch (_) { /* ignore */ }
  }
  // Do NOT hardcode or fallback to a foreign tenant
  return _runtimeTenantId || '';
}

export async function ensureTenantResolved(): Promise<string> {
  const current = initTenantFromUrl();
  if (current) return current;

  // Dynamic LIFF ID Reverse Lookup (Pattern 1 — No hardcoded tenant!)
  const detectedLiffId = extractLiffIdFromContext();
  if (detectedLiffId) {
    try { sessionStorage.setItem('_detected_liff_id', detectedLiffId); } catch (_) {}
    const resolved = await resolveTenantByLiff(detectedLiffId);
    if (resolved) {
      setRuntimeTenantId(resolved);
      console.log('[MiniApp Config] Dynamically resolved tenant via LIFF ID:', resolved, 'from LIFF:', detectedLiffId);
      return resolved;
    }
  }

  return _runtimeTenantId || '';
}

let _runtimeHotelCode: string | null = null;

export function setRuntimeHotelCode(hotelCode: string): void {
  if (hotelCode && typeof hotelCode === 'string') {
    _runtimeHotelCode = hotelCode;
  }
}

export function getHotelCode(): string {
  if (_runtimeHotelCode) return _runtimeHotelCode;
  return import.meta.env.VITE_HOTEL_CODE || '';
}

/**
 * Fetch tenant config from API (with caching)
 */
export async function fetchTenantConfig(): Promise<TenantAppConfig | null> {
  // Check cache first
  if (cachedTenantConfig && Date.now() < cacheExpiry) {
    return cachedTenantConfig;
  }

  try {
    const baseUrl = getApiBaseUrl();
    const tenantId = getTenantId();
    const response = await fetch(`${baseUrl}/api/v1/${tenantId}/config`);

    if (!response.ok) {
      console.error('[MiniApp Config] Failed to fetch tenant config:', response.status);
      return null;
    }

    const data = await response.json();
    if (data.success) {
      cachedTenantConfig = data.data;
      cacheExpiry = Date.now() + CACHE_TTL_MS;
      console.log('[MiniApp Config] Loaded tenant config:', cachedTenantConfig);
      return cachedTenantConfig;
    }

    return null;
  } catch (error) {
    console.error('[MiniApp Config] Error fetching tenant config:', error);
    return null;
  }
}

export function getCachedTenantConfig(): TenantAppConfig | null {
  return cachedTenantConfig;
}

/**
 * Get LIFF ID - from API (runtime) or env var (build-time fallback)
 */
export async function getLiffId(): Promise<string> {
  // 1. Direct detection from context/URL/referrer
  const detected = extractLiffIdFromContext();
  if (detected) {
    return detected;
  }

  // 2. Try runtime API from resolved tenant config
  const tenantConfig = await fetchTenantConfig();
  if (tenantConfig?.liffId) {
    return tenantConfig.liffId;
  }

  return '';
}

export const config = {
  // LIFF ID resolved async — use getLiffId() instead for runtime value
  liffId: import.meta.env.VITE_LIFF_ID || '',
  get apiBaseUrl() {
    return getApiBaseUrl();
  },
  get tenantId() {
    return getTenantId();
  },
};

// Supported languages
export const LANGUAGES = ['th', 'en', 'zh', 'ru', 'ja', 'ko'] as const;
export type Language = (typeof LANGUAGES)[number];

// Booking constants
export const MAX_ADULTS = 10;
export const MAX_CHILDREN = 10;
export const MAX_ROOMS = 10;
export const MIN_NIGHTS = 1;
export const MAX_NIGHTS = 30;
